import checkIcon from "assets/images/status-display-check-icon.svg";
import removeIcon from "assets/images/x-gray-circle-icon.svg";
import classNames from "classnames";
import formatISO from "date-fns/formatISO";
import {useState} from "react";
import {refillEmbed as strings, common as stringsCommon} from "strings";
import {v4 as uuidv4} from "uuid";

import Button from "components/Button";
import {DateString} from "components/DateInput";
import StyledDateInput from "components/StyledDateInput";
import TextInput from "components/TextInput";

import {useRefillRequestUnregistered} from "liberty-core/api/react-query/refillRequest";

import isNotNumeric from "lib/isNotNumeric";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./RefillEmbedPage.module.css";

function AdditionalRxField(props: {
  value: string;
  errorText: string;
  setValue: (value: string) => void;
  onRemove: () => void;
}) {
  return (
    <div className={classNames(styles.additionalRxContainer, styles.input)}>
      <TextInput
        colorVariant="grey"
        label={stringsCommon.rxNumber}
        type="text"
        value={props.value}
        errorText={props.errorText}
        onChangeText={props.setValue}
        smallRightPadding
      />
      <button onClick={props.onRemove} className={styles.removeButton}>
        <img
          className={styles.removeIcon}
          src={removeIcon}
          alt={strings.removeAlt}
        />
      </button>
    </div>
  );
}

export default function RefillEmbedPage() {
  const [pharmacy] = useCurrentPharmacy();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState<DateString>("");
  const [rxNumbers, setRxNumbers] = useState([
    {id: uuidv4(), value: "", errorText: ""},
  ]);
  const [errorMessage, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);

  const {mutateAsync: submitRefillRequest, isLoading: isLoading} =
    useRefillRequestUnregistered();

  function onSubmit() {
    if (isLoading) {
      return;
    }

    setErrorMessage("");
    submitRefillRequest({
      firstName,
      lastName,
      dateOfBirth: dob,
      scriptNumbers: rxNumbers.map((val) => Number(val.value)),
      pickupDateTime: formatISO(new Date()),
      pharmacyId: pharmacy?.id ?? 0,
      comment: "",
    })
      .then(() => {
        setSuccess(true);
      })
      .catch(() => {
        setErrorMessage(strings.genericError);
      });
  }

  if (success) {
    return (
      <div className={classNames(styles.container, styles.successContainer)}>
        <img className={styles.successCheck} src={checkIcon} alt="" />
        <div className={styles.successMessage}>{strings.success}</div>
        <Button
          colorVariant="grey"
          className={styles.requestMoreButton}
          variant="unfilled"
          onClick={() => {
            setFirstName("");
            setLastName("");
            setDob("");
            setRxNumbers([{id: uuidv4(), value: "", errorText: ""}]);
            setSuccess(false);
          }}
        >
          {strings.requestMore}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.title}>{strings.medicationRefill}</div>
      <div className={styles.flexContainer}>
        <TextInput
          colorVariant="grey"
          className={styles.input}
          label={stringsCommon.firstName}
          type="text"
          autoComplete="given-name"
          value={firstName}
          onChangeText={setFirstName}
          smallRightPadding
        />
        <TextInput
          colorVariant="grey"
          className={styles.input}
          label={stringsCommon.lastName}
          type="text"
          autoComplete="family-name"
          value={lastName}
          onChangeText={setLastName}
          smallRightPadding
        />
        <StyledDateInput
          colorVariant="grey"
          className={styles.input}
          label={stringsCommon.dateOfBirth}
          autoComplete="bday"
          value={dob}
          onChangeDate={setDob}
        />
        <TextInput
          colorVariant="grey"
          className={styles.input}
          label={stringsCommon.rxNumber}
          type="text"
          value={rxNumbers[0].value}
          errorText={rxNumbers[0].errorText}
          onChangeText={(text) => {
            const errorText = isNotNumeric(text)
              ? strings.rxNumberNotANumber
              : "";
            setRxNumbers((prev) => [
              {value: text, id: prev[0].id, errorText},
              ...prev.slice(1, prev.length),
            ]);
          }}
          smallRightPadding
        />
        {rxNumbers.map((_, i) => {
          if (i === 0) return null;
          return (
            <AdditionalRxField
              key={rxNumbers[i].id}
              value={rxNumbers[i].value}
              errorText={rxNumbers[i].errorText}
              setValue={(text) => {
                const errorText = isNotNumeric(text)
                  ? strings.rxNumberNotANumber
                  : "";
                setRxNumbers((prev) => [
                  ...prev.slice(0, i),
                  {value: text, id: prev[i].id, errorText},
                  ...prev.slice(i + 1, prev.length),
                ]);
              }}
              onRemove={() =>
                setRxNumbers((prev) =>
                  prev.slice(0, i).concat(prev.slice(i + 1, prev.length)),
                )
              }
            />
          );
        })}
      </div>
      <div className={styles.addAnotherContainer}>
        <Button
          className={styles.addAnother}
          variant="minimal"
          disabled={rxNumbers[0].value === ""}
          onClick={() => {
            setRxNumbers((prev) => [
              ...prev,
              {value: "", id: uuidv4(), errorText: ""},
            ]);
          }}
        >
          {strings.enterAnother}
        </Button>
      </div>
      {errorMessage ? (
        <div className={styles.errorMessage}>{errorMessage}</div>
      ) : null}
      <Button
        colorVariant="grey"
        loading={isLoading}
        disabled={
          !firstName ||
          !lastName ||
          !dob ||
          rxNumbers.some((r) => r.value === "" || isNotNumeric(r.value))
        }
        // eslint-disable-next-line react/jsx-no-bind
        onClick={onSubmit}
      >
        {strings.requestRefill}
      </Button>
    </div>
  );
}
